import { useEffect, useState } from "react";
import { type Connector, useAccount, useDisconnect } from "wagmi";

import { LinkWalletModal } from "@/components/wallets/LinkWalletModal";
import { WalletConnectorsModal } from "@/components/wallets/WalletConnectorsModal";
import {
  type ImportOrigin,
  useAssetImport,
} from "@/context/AssetImportProvider";
import { useCollectionsQuery, useLinkWallet } from "@/hooks";
import { useValidConnectors } from "@/hooks/useValidConnectors";
import type { LinkWalletError, LinkWalletState, Wallet } from "@/types";
import { notifyError } from "@/utils/monitoring";
import { analytics } from "@analytics";

const SETTLED_TIMEOUT = 1500;

type LinkedAddress = {
  address: string;
  successScreenShownTime: number;
};

export const LinkWallet = ({
  isConnectorsModalOpen,
  onCloseConnectorsModal,
  onLinkPushPath,
  linkedWallets,
  showImportOptions,
  onLinkAnother,
  origin,
}: {
  isConnectorsModalOpen: boolean;
  onCloseConnectorsModal: () => void;
  onLinkPushPath?: { pathname?: string } | undefined;
  linkedWallets?: Wallet[];
  showImportOptions?: boolean;
  onLinkAnother?: () => void;
  origin: ImportOrigin;
}) => {
  const { mutate: link, reset: resetLinkWallet } = useLinkWallet();
  const [error, setError] = useState<LinkWalletError>();
  const [state, setState] = useState<LinkWalletState>(undefined);
  const [connector, setConnector] = useState<Connector>();
  const { onlyWalletConnectAvailable, validConnectors } = useValidConnectors();
  const { disconnectAsync } = useDisconnect();
  const { isConnected, connector: activeConnector } = useAccount();
  const [linkedAddress, setLinkedAddress] = useState<LinkedAddress | undefined>(
    undefined,
  );
  const { startImport } = useAssetImport();
  const {
    zkEvmData,
    zkEvmIsLoading,
    zkEvmError,
    starkExData,
    starkExIsLoading,
    starkExError,
  } = useCollectionsQuery(linkedAddress?.address, linkedAddress !== undefined);

  const collectionsIsLoading = zkEvmIsLoading || starkExIsLoading;
  const collectionsError = zkEvmError || starkExError;

  const onLinkingRequest = () => {
    setState("LINKING");
    analytics.track(
      "PassportDashboardWalletsExternalWallets_LinkWalletApiEffectStarted",
    );
  };

  useEffect(() => {
    if (state === "SUCCESS" && !collectionsIsLoading && linkedAddress) {
      const timeOnSuccessScreen =
        Date.now() - linkedAddress.successScreenShownTime;
      setTimeout(
        () => {
          const hasZkEvmCollections =
            zkEvmData && zkEvmData.pages[0].collections.length > 0;
          if (hasZkEvmCollections) {
            if (!showImportOptions) {
              startImport?.(
                onLinkPushPath,
                linkedAddress.address,
                origin,
                false,
              );
            }
            setState(showImportOptions ? "IMPORT" : undefined);
            analytics.track(
              "PassportDashboardWalletsExternalWallets_LinkedWalletImportEffect",
            );
          } else if (collectionsError) {
            setState(undefined);
          } else {
            analytics.track(
              "PassportDashboardWalletsExternalWallets_LinkedWalletEmptyEffect",
            );
            setState("EMPTY_WALLET");
          }
        },
        // Ensure success is shown for a minimum of SETTLED_TIMEOUT so its not jarring
        timeOnSuccessScreen < SETTLED_TIMEOUT
          ? SETTLED_TIMEOUT - timeOnSuccessScreen
          : 0,
      );
    }
  }, [
    collectionsError,
    collectionsIsLoading,
    linkedAddress,
    zkEvmData,
    state,
    origin,
    onLinkPushPath,
    showImportOptions,
    startImport,
  ]);

  useEffect(() => {
    if (state === undefined) {
      setLinkedAddress(undefined);
    }
  }, [state]);

  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const onSettledSuccess = (data: any) => {
    analytics.track(
      "PassportDashboardWalletsExternalWallets_LinkNewWalletEffectSucceeded",
      { walletId: connector?.id },
    );
    setState("SUCCESS");
    setLinkedAddress({
      address: data.address,
      successScreenShownTime: Date.now(),
    });
  };

  const onSettledFailed = (error: LinkWalletError) => {
    analytics.track(
      "PassportDashboardWalletsExternalWallets_LinkNewWalletEffectFailed",
      { error: error.code, walletId: connector?.id },
    );
    setState(undefined);
    setError(error);
    notifyError(error, "linkWallet");
  };

  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const onSettled = (data: any, error: any) => {
    data !== undefined ? onSettledSuccess(data) : onSettledFailed(error);
  };

  const onConnectorClick = (connector: Connector) => {
    resetLinkWallet();
    onCloseConnectorsModal();
    setState("WAITING");
    setError(undefined);
    analytics.track(
      "PassportDashboardWalletsExternalWallets_ConnectorListItemButtonPressed",
      { walletId: connector?.id },
    );
    link({ connector, linkedWallets, onLinkingRequest }, { onSettled });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const autoConnect = async () => {
      if (onlyWalletConnectAvailable && isConnectorsModalOpen) {
        if (isConnected) {
          try {
            await disconnectAsync({ connector: activeConnector });
          } catch (error) {
            console.error("Error during disconnectAsync", error);
          }
        }
        onConnectorClick(validConnectors[0]);
      }
    };
    autoConnect();
  }, [isConnectorsModalOpen]);

  return (
    <>
      {!onlyWalletConnectAvailable && (
        <WalletConnectorsModal
          isOpen={isConnectorsModalOpen}
          onClose={onCloseConnectorsModal}
          validConnectors={validConnectors}
          onConnectorClick={(connector) => {
            setConnector(connector);
            onConnectorClick(connector);
          }}
        />
      )}
      <LinkWalletModal
        error={error}
        state={state}
        onAfterRetry={() => connector && onConnectorClick(connector)}
        onAfterClose={() => setState(undefined)}
        onConfirm={() => {
          if (linkedAddress) {
            startImport?.(onLinkPushPath, linkedAddress.address, origin, false);
          }
        }}
        onLinkAnother={() => {
          setState(undefined);
          if (onLinkAnother) {
            onLinkAnother();
          }
        }}
      />
    </>
  );
};
